import * as React from "react";
import { Box, Grid, Typography, TextField, FormControl, InputLabel, Select, MenuItem } from "@mui/material";
import cookie from "../../../API/cookie";
import Api from "../../../API/API";
import {buildStimJsonFromBlocks} from "./stimRecipes";

var defaultValue;

let user_id = cookie.getCookie("userAccount")
  ? cookie.getCookie("userAccount")
  : "";
var api_token = cookie.getCookie("accessToken");

if (user_id) {
  defaultValue = { key: api_token };
}

/*
 * 자극 관리(Stimulus Manager) - 커스텀 자극 생성기 UI
 *
 * - 실제 자극 실행/통신 로직은 상위(ExperimentMachineListPageMiddle2)에서 처리
 * - 이 컴포넌트는 입력/편집 UI만 담당
 */
export default function StimulusManagerPage({ CardBtn, onStartCustom }) {
  const emptyStimRow = { width: "", duration: "", amp: "", hold: "" };

  const [customLoops, setCustomLoops] = React.useState([
    { repeat: 1, stims: [{ ...emptyStimRow }] },
  ]);

  // 현재 로그인 유저 role (슈퍼관리자=1만 access_role=1 설정 가능)
  // 쿠키값이 "true"(구 boolean) 또는 "1"/"2"(신 integer) 모두 처리
  const _rawIsStaff = String(cookie.getCookie("is_staff") ?? "0").toLowerCase();
  const currentUserRole = _rawIsStaff === "true" || _rawIsStaff === "1" ? 1
    : _rawIsStaff === "2" ? 2
    : 0;
  const [accessRole, setAccessRole] = React.useState(2);

  const handleAddLoop = () => {
    setCustomLoops((prev) => [
      ...prev,
      { repeat: 1, stims: [{ ...emptyStimRow }] },
    ]);
  };

  const handleRemoveLoop = (loopIndex) => {
    setCustomLoops((prev) => {
      if (prev.length === 1) return prev;
      return prev.filter((_, idx) => idx !== loopIndex);
    });
  };

  const handleLoopRepeatChange = (loopIndex) => (e) => {
    const value = e.target.value;
    setCustomLoops((prev) =>
      prev.map((loop, idx) =>
        idx === loopIndex ? { ...loop, repeat: value } : loop
      )
    );
  };

  const handleAddStimInLoop = (loopIndex) => {
    setCustomLoops((prev) =>
      prev.map((loop, idx) =>
        idx === loopIndex
          ? { ...loop, stims: [...loop.stims, { ...emptyStimRow }] }
          : loop
      )
    );
  };

  const handleRemoveStimInLoop = (loopIndex, stimIndex) => {
    setCustomLoops((prev) =>
      prev.map((loop, idx) => {
        if (idx !== loopIndex) return loop;
        if (loop.stims.length === 1) return loop;
        return {
          ...loop,
          stims: loop.stims.filter((_, sIdx) => sIdx !== stimIndex),
        };
      })
    );
  };

  const handleStimFieldChange = (loopIndex, stimIndex, field) => (e) => {
    const value = e.target.value;
    setCustomLoops((prev) =>
      prev.map((loop, idx) => {
        if (idx !== loopIndex) return loop;
        const newStims = loop.stims.map((stim, sIdx) =>
          sIdx === stimIndex ? { ...stim, [field]: value } : stim
        );
        return { ...loop, stims: newStims };
      })
    );
  };

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      color: "white",
      "& fieldset": { borderColor: "#CCCCCC" },
      "&:hover fieldset": { borderColor: "#FFFFFF" },
      "&.Mui-focused fieldset": { borderColor: "#FFFFFF" },
    },
  };

  const handleStart = () => {
    if (typeof onStartCustom === "function") {
      onStartCustom(customLoops);
    }
  };

  
  const exportStimulation = async () => {
    const name = window.prompt("자극 이름을 입력하세요.", "custom");
    const description = window.prompt(
      "자극 간단한 설명을 입력하세요.",
      "custom"
    );

    if (name === null) return null;
    if (description === null) return null;

    const blocksSpec = [];

    for (let loopIndex = 0; loopIndex < customLoops.length; loopIndex++) {
      const loop = customLoops[loopIndex];
      const repeatNum = Number(loop.repeat);

      if (!repeatNum || repeatNum <= 0) {
        alert(`Loop ${loopIndex + 1}: Repeat 값을 확인해주세요.`);
        return null;
      }

      const stims = [];

      for (let stimIndex = 0; stimIndex < loop.stims.length; stimIndex++) {
        const s = customLoops[loopIndex].stims[stimIndex];
        const fields = ["width", "duration", "amp", "hold"];

        for (const f of fields) {
          if (s[f] === "" || s[f] == null) {
            alert(
              `Loop ${loopIndex + 1}, Stim ${stimIndex + 1}: ${f} 값이 비어있습니다.`
            );
            return null;
          }
        }

        const width = Number(s.width);
        const duration = Number(s.duration);
        const amp = Number(s.amp);
        const hold = Number(s.hold);

        if (
          [width, duration, amp, hold].some(
            (v) => !Number.isFinite(v) || v < 0
          )
        ) {
          alert(
            `Loop ${loopIndex + 1}, Stim ${stimIndex + 1}: 숫자 값을 확인해주세요.`
          );
          return null;
        }

        stims.push([width, duration, amp, hold]);
      }

      if (stims.length > 0) {
        blocksSpec.push({ repeat: repeatNum, stims });
      }
    }

    if (blocksSpec.length === 0) {
      alert("유효한 자극이 없습니다.");
      return null;
    }

    const recipeJson = buildStimJsonFromBlocks(blocksSpec);

    console.log("recipeJson:", JSON.stringify(recipeJson, null, 2));

    const obj = {
      name,
      description,
      recipe: recipeJson,
      access_role: accessRole,
    };

    try {
      await Api.getPostRecipe(obj, defaultValue);
      alert("자극이 등록되었습니다.");
      window.location.reload();
    } catch (e) {
      console.error("[API] getPostRecipe error", e);
      alert("자극 등록 중 오류가 발생했습니다. 다시 시도해주세요.");
    }

    return recipeJson;
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Typography
        sx={{ fontSize: 22, fontFamily: "GmarketSansMedium" }}
        color="white"
      >
        자극 관리
      </Typography>

      <Box sx={{ mt: 2, color: "#CCCCCC", lineHeight: 1.8 }}>
        <Typography sx={{ fontSize: 16 }} color="#CCCCCC">
          커스텀 자극 생성기
        </Typography>
        <Typography sx={{ fontSize: 14 }} color="#CCCCCC">
          - 모든 값을 다 기입할 것
        </Typography>
        <Typography sx={{ fontSize: 14 }} color="#CCCCCC">
          - Duration(기간)과 Width(폭)은 논리적으로 맞는 값으로 입력 권장
        </Typography>
        <Typography sx={{ fontSize: 14 }} color="#CCCCCC">
          - Amplitude는 최대 6000 (6mA) 까지 가능
        </Typography>
        <Typography sx={{ fontSize: 14 }} color="#CCCCCC">
          - Holds(지속시간)은 최대 7200초(2시간) 까지 가능
        </Typography>
      </Box>

      <Box sx={{ mt: 3 }}>
        {customLoops.map((loop, loopIndex) => (
          <Box
            key={loopIndex}
            sx={{
              border: "1px solid #555",
              borderRadius: 2,
              padding: 2,
              marginBottom: 2,
            }}
          >
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  label={`Loop ${loopIndex + 1} Repeat`}
                  placeholder="반복 횟수"
                  value={loop.repeat}
                  onChange={handleLoopRepeatChange(loopIndex)}
                  InputLabelProps={{ style: { color: "#CCCCCC" } }}
                  sx={textFieldSx}
                />
              </Grid>
              <Grid item xs={12} sm="auto">
                <button
                  type="button"
                  onClick={() => handleRemoveLoop(loopIndex)}
                  style={{
                    marginLeft: 8,
                    padding: "6px 12px",
                    backgroundColor: "#555",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Loop 삭제
                </button>
              </Grid>
            </Grid>

            {loop.stims.map((stim, stimIndex) => (
              <Grid
                container
                spacing={1}
                key={stimIndex}
                alignItems="center"
                sx={{ marginTop: 1 }}
              >
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Width (μs)"
                    placeholder="예: 250"
                    value={stim.width}
                    onChange={handleStimFieldChange(loopIndex, stimIndex, "width")}
                    InputLabelProps={{ style: { color: "#CCCCCC" } }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Duration (ms)"
                    placeholder="예: 50"
                    value={stim.duration}
                    onChange={handleStimFieldChange(
                      loopIndex,
                      stimIndex,
                      "duration"
                    )}
                    InputLabelProps={{ style: { color: "#CCCCCC" } }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Amplitude (μA)"
                    placeholder="예: 3000"
                    value={stim.amp}
                    onChange={handleStimFieldChange(loopIndex, stimIndex, "amp")}
                    InputLabelProps={{ style: { color: "#CCCCCC" } }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    label="Hold (s)"
                    placeholder="예: 30"
                    value={stim.hold}
                    onChange={handleStimFieldChange(loopIndex, stimIndex, "hold")}
                    InputLabelProps={{ style: { color: "#CCCCCC" } }}
                    sx={textFieldSx}
                  />
                </Grid>
                <Grid item xs={12} sm="auto">
                  <button
                    type="button"
                    onClick={() => handleRemoveStimInLoop(loopIndex, stimIndex)}
                    style={{
                      marginLeft: 8,
                      padding: "6px 12px",
                      backgroundColor: "#555",
                      color: "white",
                      border: "none",
                      borderRadius: 4,
                      cursor: "pointer",
                    }}
                  >
                    자극 삭제
                  </button>
                </Grid>
              </Grid>
            ))}

            <Grid container sx={{ marginTop: 1 }}>
              <Grid item>
                <button
                  type="button"
                  onClick={() => handleAddStimInLoop(loopIndex)}
                  style={{
                    padding: "6px 12px",
                    backgroundColor: "#777",
                    color: "white",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  자극 블럭 추가 +
                </button>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            {CardBtn ? (
              <CardBtn onClick={handleAddLoop}>Loop 블럭 추가 +</CardBtn>
            ) : (
              <button type="button" onClick={handleAddLoop}>
                Loop 블럭 추가 +
              </button>
            )}
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {CardBtn ? (
              <CardBtn onClick={handleStart}>커스텀 자극 시작</CardBtn>
            ) : (
              <button type="button" onClick={handleStart}>
                커스텀 자극 시작
              </button>
            )}
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            {CardBtn ? (
              <CardBtn onClick={exportStimulation}>자극 레시피 추가(Export)</CardBtn>
            ) : (
              <button type="button" onClick={exportStimulation}>
                자극 export
              </button>
            )}
          </Grid>

          {/* access_role 셀렉터: Export 버튼 오른쪽 */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel sx={{ color: "#CCCCCC" }}>레시피 접근 권한</InputLabel>
              <Select
                label="레시피 접근 권한"
                value={accessRole}
                onChange={(e) => setAccessRole(e.target.value)}
                sx={{
                  color: "white",
                  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#CCCCCC" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#FFFFFF" },
                  "& .MuiSvgIcon-root": { color: "white" },
                }}
              >
                {currentUserRole === 1 && (
                  <MenuItem value={1}>슈퍼관리자 전용</MenuItem>
                )}
                <MenuItem value={2}>전체 관리자 공개</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
}
